import React, { useState, useEffect, useRef, useCallback } from 'react';
import { documentsApi } from '../../api/documents';
import toast from 'react-hot-toast';
import { Brain, Loader2, ChevronDown, ChevronRight, Eye } from 'lucide-react';

const CATEGORIES = [
  { value: 'uncategorized', label: 'General' },
  { value: 'legal', label: 'Legal' },
  { value: 'medical', label: 'Medical' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'billing', label: 'Billing' },
  { value: 'intake', label: 'Intake' },
  { value: 'id', label: 'ID / Personal' },
];

const AI_CATEGORY_COLORS = {
  medical_record: 'bg-red-100 text-red-700',
  medical_bill: 'bg-red-100 text-red-700',
  insurance_policy: 'bg-blue-100 text-blue-700',
  insurance_correspondence: 'bg-blue-100 text-blue-700',
  legal_pleading: 'bg-purple-100 text-purple-700',
  legal_correspondence: 'bg-purple-100 text-purple-700',
  police_report: 'bg-orange-100 text-orange-700',
  accident_report: 'bg-orange-100 text-orange-700',
  demand_letter: 'bg-yellow-100 text-yellow-700',
  settlement_agreement: 'bg-green-100 text-green-700',
  lien: 'bg-pink-100 text-pink-700',
  photo_evidence: 'bg-cyan-100 text-cyan-700',
  employment_record: 'bg-indigo-100 text-indigo-700',
  wage_loss: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-600',
};

function formatCategoryLabel(cat) {
  if (!cat) return '';
  return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const MIME_ICONS = {
  'application/pdf': '📄',
  'image/jpeg': '🖼️', 'image/png': '🖼️', 'image/gif': '🖼️', 'image/webp': '🖼️',
  'application/msword': '📝', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.ms-excel': '📊', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'text/plain': '📃', 'text/csv': '📊',
  'application/zip': '🗜️',
};

function formatSize(bytes) {
  const b = parseInt(bytes);
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function DocumentPanel({ recordId }) {
  const [documents, setDocuments] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('uncategorized');
  const [collapsed, setCollapsed] = useState({});
  const [processing, setProcessing] = useState({}); // { [docId]: true }
  const [ocrExpanded, setOcrExpanded] = useState({}); // { [docId]: ocrData }
  const fileRef = useRef();

  const fetchDocs = useCallback(async () => {
    try {
      const data = await documentsApi.getForRecord(recordId);
      setDocuments(data.documents);
      setGrouped(data.grouped);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally { setLoading(false); }
  }, [recordId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        await documentsApi.upload(files[0], recordId, uploadCategory);
        toast.success('File uploaded');
      } else {
        await documentsApi.uploadMultiple(Array.from(files), recordId, uploadCategory);
        toast.success(files.length + ' files uploaded');
      }
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDownload = async (doc) => {
    try {
      const data = await documentsApi.getDownloadUrl(doc.id);
      window.open(data.url, '_blank');
    } catch (err) { toast.error('Download failed'); }
  };

  const handlePreview = async (doc) => {
    try {
      const data = await documentsApi.getPreviewUrl(doc.id);
      if (data.previewable) {
        window.open(data.url, '_blank');
      } else {
        handleDownload(doc);
      }
    } catch (err) { toast.error('Preview failed'); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Delete "' + doc.original_name + '"?')) return;
    try {
      await documentsApi.delete(doc.id);
      toast.success('File deleted');
      fetchDocs();
    } catch (err) { toast.error('Delete failed'); }
  };

  const handleProcess = async (doc) => {
    setProcessing(prev => ({ ...prev, [doc.id]: true }));
    try {
      const result = await documentsApi.processDocument(doc.id, recordId);
      if (result.success) {
        toast.success(`Categorized as: ${formatCategoryLabel(result.category)}`);
        fetchDocs();
      } else {
        toast.error(result.error || 'Processing failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Processing failed');
    } finally {
      setProcessing(prev => ({ ...prev, [doc.id]: false }));
    }
  };

  const toggleOcr = async (doc) => {
    if (ocrExpanded[doc.id]) {
      setOcrExpanded(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
      return;
    }
    try {
      const data = await documentsApi.getOcrResults(doc.id);
      setOcrExpanded(prev => ({ ...prev, [doc.id]: data.document }));
    } catch (err) {
      toast.error('Failed to load OCR results');
    }
  };

  const toggleCat = (cat) => setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));

  if (loading) return <div className="bg-white rounded-xl border border-gray-200 p-4"><p className="text-sm text-gray-400">Loading documents...</p></div>;

  const catOrder = CATEGORIES.map(c => c.value);
  const sortedCats = Object.keys(grouped).sort((a, b) => catOrder.indexOf(a) - catOrder.indexOf(b));

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
          <span className="text-xs text-gray-500">{documents.length}</span>
        </div>
        <button onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700">
          📎 Upload
        </button>
      </div>

      {/* Drop zone + upload controls */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`mx-3 mt-3 mb-2 border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
          dragOver ? 'border-brand-400 bg-brand-50' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <input ref={fileRef} type="file" multiple className="hidden"
          onChange={(e) => handleUpload(e.target.files)} />
        {uploading ? (
          <p className="text-xs text-gray-500">⏳ Uploading...</p>
        ) : (
          <div>
            <p className="text-xs text-gray-500">Drop files here or <button onClick={() => fileRef.current?.click()} className="text-brand-600 font-medium hover:underline">browse</button></p>
            <div className="mt-2 flex items-center justify-center gap-2">
              <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* File list grouped by category */}
      <div className="px-3 pb-3 max-h-[400px] overflow-y-auto">
        {documents.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-3">No documents yet</p>
        ) : (
          sortedCats.map(cat => {
            const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
            const docs = grouped[cat];
            const isCollapsed = collapsed[cat];

            return (
              <div key={cat} className="mb-2">
                <button onClick={() => toggleCat(cat)}
                  className="flex items-center gap-1 w-full text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-1">
                  <span className="text-[10px]">{isCollapsed ? '▸' : '▾'}</span>
                  {catLabel} ({docs.length})
                </button>
                {!isCollapsed && docs.map(doc => (
                  <div key={doc.id} className="mb-1">
                    <div className="flex items-start gap-2 py-2 px-2 rounded-lg hover:bg-gray-50 group">
                      <span className="text-base flex-shrink-0 mt-0.5">{MIME_ICONS[doc.mime_type] || '📎'}</span>
                      <div className="flex-1 min-w-0">
                        <button onClick={() => handlePreview(doc)}
                          className="text-xs font-medium text-gray-900 hover:text-brand-600 truncate block w-full text-left">
                          {doc.original_name}
                        </button>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-[10px] text-gray-400">{formatSize(doc.size_bytes)}</span>
                          <span className="text-[10px] text-gray-400">{formatDate(doc.created_at)}</span>
                          {doc.uploaded_by_first && (
                            <span className="text-[10px] text-gray-400">{doc.uploaded_by_first}</span>
                          )}
                          {doc.document_category && (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${AI_CATEGORY_COLORS[doc.document_category] || AI_CATEGORY_COLORS.other}`}
                              title={doc.ai_summary || ''}>
                              {formatCategoryLabel(doc.document_category)}
                            </span>
                          )}
                          {doc.ai_confidence != null && (
                            <span className="text-[10px] text-gray-400">{Math.round(doc.ai_confidence * 100)}%</span>
                          )}
                        </div>
                        {/* AI metadata chips */}
                        {doc.ai_metadata && (doc.ai_metadata.key_dates?.length > 0 || doc.ai_metadata.key_parties?.length > 0 || doc.ai_metadata.dollar_amounts?.length > 0) && (
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {doc.ai_metadata.key_dates?.slice(0, 3).map((d, i) => (
                              <span key={i} className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{d}</span>
                            ))}
                            {doc.ai_metadata.dollar_amounts?.slice(0, 3).map((a, i) => (
                              <span key={i} className="text-[9px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{a}</span>
                            ))}
                            {doc.ai_metadata.key_parties?.slice(0, 2).map((p, i) => (
                              <span key={i} className="text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{p}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {processing[doc.id] ? (
                          <Loader2 className="w-3.5 h-3.5 text-brand-500 animate-spin" />
                        ) : (
                          <button onClick={() => handleProcess(doc)}
                            title={doc.ocr_processed_at ? 'Re-process with AI' : 'Process with AI'}
                            className="text-xs p-1 text-gray-400 hover:text-brand-600">
                            <Brain className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {doc.ocr_processed_at && (
                          <button onClick={() => toggleOcr(doc)} title="View OCR text"
                            className="text-xs p-1 text-gray-400 hover:text-brand-600">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => handleDownload(doc)} title="Download"
                          className="text-xs p-1 text-gray-400 hover:text-brand-600">⬇</button>
                        <button onClick={() => handleDelete(doc)} title="Delete"
                          className="text-xs p-1 text-gray-400 hover:text-red-600">✕</button>
                      </div>
                    </div>
                    {/* OCR text expandable panel */}
                    {ocrExpanded[doc.id] && (
                      <div className="mx-2 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                        {ocrExpanded[doc.id].ai_summary && (
                          <p className="text-xs text-gray-700 mb-2 italic">{ocrExpanded[doc.id].ai_summary}</p>
                        )}
                        <pre className="text-[10px] text-gray-600 whitespace-pre-wrap max-h-[200px] overflow-y-auto font-mono">
                          {ocrExpanded[doc.id].ocr_text || 'No OCR text available'}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
